import { useState, useMemo, useCallback } from "react";
import {
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  useReactTable,
  type SortingState,
  Row,
} from "@tanstack/react-table";
import { BaseStudentResponseDto } from "@packages/api/madlenAPI.schemas";
import { useStudentTableColumns } from "./StudentTableColumns";
import { useGetClassroomInfoAsTeacherForAssignViewPage } from "@packages/api/madlenAPI";
import { GenericTable } from "@core/table";

interface StudentTableProps {
  selectedClassId: number | undefined;
  searchQuery: string;
}

export const StudentTable = ({
  selectedClassId,
  searchQuery,
}: StudentTableProps) => {
  const [sorting, setSorting] = useState<SortingState>([
    {
      id: "name",
      desc: false,
    },
    {
      id: "surname",
      desc: false,
    },
    {
      id: "email",
      desc: false,
    },
  ]);

  const queryParams = useMemo(() => ({}), []);

  const {
    data: students,
    isLoading,
    error,
  } = useGetClassroomInfoAsTeacherForAssignViewPage(
    selectedClassId || 0,
    queryParams,
  );

  const tableData = useMemo(
    () => students?.students ?? [],
    [JSON.stringify(students)],
  );

  const columns = useStudentTableColumns();

  const table = useReactTable({
    data: tableData,
    columns,
    state: {
      sorting,
      globalFilter: searchQuery,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    globalFilterFn: useCallback(
      (
        row: Row<BaseStudentResponseDto>,
        columnId: string,
        filterValue: string,
      ) => {
        const value = row.getValue(columnId);
        if (typeof value === "string") {
          return value.toLowerCase().includes(filterValue.toLowerCase());
        }
        if (typeof value === "number") {
          return value.toString().includes(filterValue);
        }
        return false;
      },
      [],
    ),
    enableSorting: true,
  });

  return (
    <GenericTable
      table={table}
      isLoading={isLoading}
      error={error}
      isEmpty={!students?.students?.length}
    />
  );
};
